import { usePetitionDetailsPageData } from 'src/app/pages/petitionGamma/details/pageContext';

import type { BuildingBlockDms, NormalizedDecisionMaker } from '../../shared/types';

import {
	formatAllBuckets,
	getLegislativeBodies,
	getPrimaryDms,
	getResponseText,
	normalizeDms,
	sortAllDms,
	sortIndividualDms,
} from './utils';

type Result = ModelHookResult<{
	sortedAllDms: BuildingBlockDms;
	displayViewAllButton: boolean;
}>;

// eslint-disable-next-line max-lines-per-function
export function useDecisionMakers(): Result {
	const {
		data: {
			petition: {
				primaryDecisionMakerConnection: { nodes: dms },
				dmResponsesConnection: { nodes: allDmResponses },
			},
		},
	} = usePetitionDetailsPageData();

	const normalizedAll = normalizeDms(dms, allDmResponses);
	const politiciansOnly = normalizedAll.filter((dm: NormalizedDecisionMaker) => dm.type === 'POLITICIAN');

	// One bucket: primary DMs
	const sortedPrimaryDMs = sortIndividualDms(getPrimaryDms(politiciansOnly));

	// One bucket: legislative bodies
	const primaryDmIds = new Set(sortedPrimaryDMs.map((dm) => dm.id));
	const politiciansExcludingPrimaries = politiciansOnly.filter((dm) => !primaryDmIds.has(dm.id));
	const legislativeBodiesList = getLegislativeBodies(politiciansExcludingPrimaries);

	const sortedLegislativeBodies = legislativeBodiesList.map((body) => {
		const sortedLBDMs = sortIndividualDms(body.groupDms);

		const hasMissing = sortedLBDMs.some((dm) => !dm.location);
		const uniqueLocations = [...new Set(sortedLBDMs.map((dm) => dm.location))];
		const location = !hasMissing && uniqueLocations.length === 1 ? uniqueLocations[0] : null;

		return {
			title: body.groupTitle,
			location,
			responsesText: getResponseText(sortedLBDMs),
			decisionMakers: sortedLBDMs,
		};
	});
	const legislativeBodyDmIds = new Set(
		sortedLegislativeBodies.flatMap((body) => body.decisionMakers.map((dm) => dm.id)),
	);

	// One bucket: politicians that are neither primary nor in a legislative body
	const individualPoliticians = politiciansOnly.filter((dm) => {
		return !legislativeBodyDmIds.has(dm.id) && !primaryDmIds.has(dm.id);
	});

	// One bucket: remaining DMs
	const remainingDMs = normalizedAll.filter((dm) => dm.type !== 'POLITICIAN');

	const allFormattedBucketsOfDms = formatAllBuckets(
		sortedPrimaryDMs,
		sortedLegislativeBodies,
		individualPoliticians,
		remainingDMs,
	);
	const sortedAllDms = sortAllDms(allFormattedBucketsOfDms);

	return {
		data: {
			sortedAllDms,
			displayViewAllButton: true,
		},
	};
}
